<template>
    <v-card class="mb-2" color="grey darken-2" @mouseover="mouseOver=true" @mouseout="mouseOver=false" :elevation="elevation">
        <v-card-text class="pa-0">

            <!-- HEADER -->
            <v-card color="#484848" tile elevation="2">
                <v-card-text class="pa-1">

                    <div class="ml-3 d-flex justify-space-between">

                        <!-- LANGUAGE FLAG AND SUPPLIER NAME -->
                        <div class="d-flex">

                            <v-avatar size="28" class="align-self-center">
                                <v-img :src="supplierLink.localeImage" style="opacity: 0.8"/>
                            </v-avatar>

                            <div class="subtitle-1 font-weight-bold ml-4 align-self-center">
                                {{ supplierLink.supplierName }}
                            </div>

                            <div v-show="!supplierLink.enabled" class="align-self-center ml-3">
                                <v-chip label small color="red darken-2">DISABLED</v-chip>
                            </div>

                        </div>

                        <!-- SUB PANEL LABEL AND SWITCH -->
                        <div class="align-self-center d-flex">

                            <div class="align-self-center">
                                {{ supplierLink.subPanelName }}
                            </div>

                            <v-chip label color="grey darken-2" class="ml-4 grey--text text--lighten-2 align-self-center">
                                {{ supplierLink.localeCode }}
                            </v-chip>

                            <div class="align-self-center pl-4">
                                <v-tooltip color="primary" top open-delay="500">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-switch dense
                                                      hide-details
                                                      v-model="data.enabled"
                                                      @change="emit()"
                                                      class="mt-0">
                                            </v-switch>
                                        </div>
                                    </template>
                                    <span v-if="supplierLink.enabled">Click here to disable supplier
                                        link.</span>
                                    <span v-else>Click here to enable supplier</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </div>

                </v-card-text>
            </v-card>

            <div class="d-flex pl-3 pr-3">

                <div class="align-self-center" v-show="supplierLink.max_completes > 0">

                    <v-tooltip color="primary" top open-delay="250">
                        <template v-slot:activator="{ on }">

                            <v-progress-circular class="ml-4 mr-8"
                                                 v-on="on"
                                                 :size="100"
                                                 :width="12"
                                                 color="green darken-2"
                                                 :value="supplierLink.progressPercent">
                                <span class="white--text">
                                    {{ supplierLink.progressPercent }}%
                                </span>
                            </v-progress-circular>

                        </template>
                        Total number of completes: {{ supplierLink.completedCount }}
                    </v-tooltip>
                </div>

                <div class="align-self-center flex-grow-1 pb-3 mr-2">

                    <!-- EXAMPLE LINK -->
                    <div class="align-self-center d-flex">
                        <div class="align-self-center flex-grow-1" @click="copy(supplierLink)">
                            <v-tooltip color="primary" top open-delay="500">
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        <v-text-field v-model="supplierLink.url"
                                                      class="align-self-center body-2"
                                                      disabled
                                                      persistent-hint
                                                      hint="Entry link"
                                                      prepend-icon="mdi-link"
                                        ></v-text-field>
                                    </div>
                                </template>
                                Click anywhere on the link to copy it to clipboard
                            </v-tooltip>
                        </div>

                        <!-- TEST LINK BUTTON -->
                        <div class="align-self-center ml-4">
                            <v-tooltip color="primary" top>
                                <template v-slot:activator="{ on }">
                                    <v-btn fab
                                           x-small
                                           v-on="on"
                                           :disabled="!saved"
                                           @click="testEntryLinkClicked()"
                                           elevation="0"
                                           color="primary">
                                        <v-icon>mdi-link</v-icon>
                                    </v-btn>
                                </template>
                                <span>Test entry link</span>
                            </v-tooltip>
                        </div>
                    </div>

                    <!-- MAX COMPLETES -->
                    <div class="d-flex">
                        <div class="align-self-center">
                            <v-icon color="grey">mdi-chart-donut</v-icon>
                        </div>
                        <div class="align-self-center ml-2">
                            <v-text-field dense
                                          persistent-hint hint="Maximum completes"
                                          v-model="data.max_completes"
                                          @input="emit()"
                            ></v-text-field>
                        </div>
                    </div>
                </div>
            </div>

            <json v-if="false" :data="supplierLink"/>

        </v-card-text>

    </v-card>
</template>

<script>

import Json from 'vue-json-pretty';
import {clipboardMixin} from "../../../../components/mixins/clipboardMixin";
import {v4 as uuid} from 'uuid';
import {mapState, mapActions} from 'vuex';

const vuexModule = 'ProjectManager';

export default {

    mixins: [clipboardMixin],

    components: {
        Json
    },

    props: {
        // TODO: Rename to project supplier to be consistent
        supplierLink: {
            type: Object,
            required: true,
        },
        project: {
            type: Object,
            required: true,
        }
    },

    data() {
        return {
            data: {
                enabled: null,
                max_completes: null,
            },
            mouseOver: false,
        }
    },

    computed: {

        saved() {
            return !!this.project.id;
        },

        elevation() {
            return this.mouseOver ? 5 : 2;
        }
    },

    methods: {

        ...mapActions(vuexModule, [
            'getSupplierTestLink',
        ]),

        testEntryLinkClicked() {

            if (!this.saved) {
                Bus.$emit('flash-message', {type: 'error', message: 'You must save project first!'});
            } else {

                this.getSupplierTestLink({
                    projectId: this.project.id,
                    supplierId: this.supplierLink.id
                })
                    .then(response => {
                        this.sendToUrl(response.data.url, true);
                    })
                    .catch(error => {
                        Bus.$emit('flash-message', {type: 'error', message: 'OOPS! That failed, try saving the project first!'});
                    });
            }
        },

        emit() {
            this.$emit('change', {data: this.data, supplierLink: this.supplierLink});
        },

        copy(supplierLink) {

            this.copyToClipboard(supplierLink.url);
            Bus.$emit('flash-message', {type: 'success', message: 'Link copied to clipboard!'});
        }
    },

    mounted() {
        this.data.enabled = this.supplierLink.enabled;
        this.data.max_completes = this.supplierLink.max_completes;
    },
}
</script>